import { ipfsToHttp } from "@/lib/image-upload";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { dropQueryOptions, listingsQueryOptions } from "@/routes/drop.$id";

export const Route = createFileRoute("/market")({
  component: Market,
  head: () => ({
    meta: [
      { title: "Market - RobotDex" },
      { name: "description", content: "Browse live NFT drops across Robinhood Chain and EVM networks." },
    ],
  }),
});

function Market() {
  const qc = useQueryClient();
  const { data: drops, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["market-drops"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nft_drops")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(60);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });

  function warmDrop(d: any) {
    // Seed drop cache from row we already have to guarantee consistency.
    qc.setQueryData(dropQueryOptions(d.id).queryKey, d);
    qc.prefetchQuery({ ...dropQueryOptions(d.id), staleTime: 15_000 });
    qc.prefetchQuery({ ...listingsQueryOptions(d.id), staleTime: 15_000 });
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <PageHeader
        title="Market"
        subtitle="Live drops from creators around the world"
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()}>Refresh</Button>
        }
      />

      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="overflow-hidden bg-card/60 animate-pulse">
              <div className="aspect-square bg-muted/30" />
              <div className="p-4 space-y-2">
                <div className="h-3 w-2/3 bg-muted/40 rounded" />
                <div className="h-2 w-1/2 bg-muted/30 rounded" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {isError && (
        <Card className="p-8 text-center neon-border bg-card/60">
          <p className="text-destructive">Failed to load market.</p>
          <p className="text-xs text-muted-foreground mt-1">{String((error as any)?.message ?? error)}</p>
          <Button onClick={() => refetch()} className="mt-4">Try again</Button>
        </Card>
      )}

      {!isLoading && !isError && (!drops || drops.length === 0) && (
        <Card className="p-10 text-center neon-border bg-card/60">
          <p className="text-muted-foreground">No drops yet. Be the first!</p>
          <Button asChild className="mt-4"><Link to="/create">Create a drop</Link></Button>
        </Card>
      )}

      {!isLoading && !isError && drops && drops.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {drops.map((d: any) => (
            <Link
              key={d.id}
              to="/drop/$id"
              params={{ id: d.id }}
              preload="intent"
              onMouseEnter={() => warmDrop(d)}
              onFocus={() => warmDrop(d)}
              onTouchStart={() => warmDrop(d)}
            >
              <Card className="overflow-hidden bg-card/60 hover:neon-border transition group">
                <div className="aspect-square bg-gradient-to-br from-primary/20 to-background overflow-hidden">
                  {d.logo_url ? (
                    <img
                      src={ipfsToHttp(d.logo_url)}
                      alt={d.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl font-black neon-text">
                      {d.symbol?.slice(0, 3)}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold truncate">{d.name}</h3>
                    <Badge variant="outline" className="neon-border">{d.symbol}</Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2 min-h-8">{d.description}</p>
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Chain {d.chain_id}</span>
                    <span className="text-primary font-semibold">
                      {d.mint_price_wei && d.mint_price_wei !== "0" ? "Paid mint" : "Free mint"}
                    </span>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
